import {findNodeByCodes, normalizeCode, isCode} from '@/utils/fsCodes';

export interface BsInventoriesForPl {
    clientCurrent: number;
    auditedCurrent: number;
    lastYearAudited: number;
}

const isNonZero = (n: number): boolean => Math.abs(Number(n) || 0) >= 0.01;

export function resolveBsInventoriesFromBsTree(bsTree: any[] | null | undefined): BsInventoriesForPl {
    const invNode = findNodeByCodes((bsTree || []) as any[], ['BS-CA-INV']);
    return {
        clientCurrent: Number((invNode as any)?.openingAmount ?? 0) || 0,
        auditedCurrent: Number((invNode as any)?.finalAmount ?? 0) || 0,
        lastYearAudited: Number((invNode as any)?.lastYearAudited ?? 0) || 0,
    };
}

/** 无存货（OI/CI 均无有效数）时隐藏 Cost of Sales，因其与 Purchases 等价 */
export function shouldHidePlCostOfSalesRow(
    bsInventories: BsInventoriesForPl,
    plTree?: any[] | null,
): boolean {
    const {clientCurrent, auditedCurrent, lastYearAudited} = bsInventories;
    if (isNonZero(clientCurrent) || isNonZero(auditedCurrent) || isNonZero(lastYearAudited)) {
        return false;
    }

    const oiNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-OI']);
    const ciNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-CI']);
    const oiLastYear = Number((oiNode as any)?.lastYearAudited ?? 0) || 0;
    if (isNonZero(oiLastYear)) return false;

    const hasCiData = isNonZero(Number((ciNode as any)?.openingAmount ?? (ciNode as any)?.amount ?? 0) || 0)
        || (Array.isArray((ciNode as any)?.adjustment)
            && (ciNode as any).adjustment.some((a: any) => isNonZero(a?.adjustment ?? 0)));
    const hasOiAdj = Array.isArray((oiNode as any)?.adjustment)
        && (oiNode as any).adjustment.some((a: any) => isNonZero(a?.adjustment ?? 0));
    return !hasCiData && !hasOiAdj;
}

export function computePlCostOfSalesAmount(
    openingInv: number,
    purchases: number,
    closingInv: number,
): number {
    return openingInv + purchases + closingInv;
}

/**
 * FS 本年首次出现 BS 存货（无上年存货 / 无 Opening inventories）时，
 * 应把 Cost of Sales 锁定为 Purchases base，并用 COS−OI−CI 反推 Purchases。
 *
 * 避免 PUR 模式下 Closing inventories 压低 COS、抬高利润，与 BS Inventories
 * 资产增加叠出与 Inventories 相关的 unbalance。
 */
export function shouldFreezeCosWhenIntroducingBsInventory(params: {
    bsInventoriesCurrent: number;
    bsInventoriesLastYear: number;
    openingInventories?: number;
}): boolean {
    if (!isNonZero(params.bsInventoriesCurrent)) return false;
    if (isNonZero(params.bsInventoriesLastYear)) return false;
    if (isNonZero(params.openingInventories ?? 0)) return false;
    return true;
}

export function filterHiddenCostOfSalesFlatRows<T extends { item: any; isSubtotal?: boolean }>(
    flatRows: T[],
    costOfSalesCodes: readonly string[],
): T[] {
    const hidden = new Set(costOfSalesCodes.map((c) => normalizeCode(c)));
    return flatRows.filter(({item, isSubtotal}) => {
        if (isSubtotal) return true;
        const code = normalizeCode((item as any)?.code);
        return !hidden.has(code);
    });
}

export type PlCotInputMode = 'COS' | 'PUR';

/**
 * adjustment 模式下，PUR 输入时 COS 为计算值：仅当 OI/CI 至少其一有值，且 OI 与 PUR 同时有值时才渲染 COS。
 */
export function shouldRenderComputedCostOfSalesInAdjustment(
    cotInputMode: PlCotInputMode,
    oiValue: number,
    ciValue: number,
    purValue: number,
): boolean {
    if (cotInputMode === 'COS') return true;
    const hasOiOrCi = isNonZero(oiValue) || isNonZero(ciValue);
    if (!hasOiOrCi) return false;
    return isNonZero(oiValue) && isNonZero(purValue);
}

/** adjustment 模式下是否隐藏 Cost of Sales 行 */
export function shouldHideCostOfSalesRowInAdjustment(
    hideCostOfSalesRow: boolean,
    cotInputMode: PlCotInputMode,
    oiValue: number,
    ciValue: number,
    purValue: number,
): boolean {
    if (hideCostOfSalesRow) return true;
    if (cotInputMode === 'COS') return false;
    return !shouldRenderComputedCostOfSalesInAdjustment(cotInputMode, oiValue, ciValue, purValue);
}

/** PL-COT 汇总：COS 行可见时用 COS，否则用 PUR */
export function resolvePlCotPrimaryCostAmount(
    showCosRow: boolean,
    cosAmount: number,
    purAmount: number,
): number {
    return showCosRow ? cosAmount : purAmount;
}

/**
 * Client / Audited 父节点（PL-COT）主成本：
 * - raw COS != 0 → 用节点本值
 * - raw COS == 0 → OI + PUR + CI 公式（即使不展示 COS 行）
 */
export function resolvePlCotCosAmountForParentRollup(params: {
    rawCos: number;
    openingInv: number;
    purchases: number;
    closingInv: number;
}): number {
    if (isNonZero(params.rawCos)) return Number(params.rawCos) || 0;
    return computePlCostOfSalesAmount(
        Number(params.openingInv) || 0,
        Number(params.purchases) || 0,
        Number(params.closingInv) || 0,
    );
}

/**
 * 仅用于 Audited / Audited(Round) 的 PL-COT 父汇总 rawCos（勿用于 Client）。
 *
 * 缩窄目标：修「PUR 录入 + PUR adj → 伴生 COS adj 被当成整段 rawCos」；
 * 不改 Client，不依赖 cotInputMode 偏好。
 *
 * - COS opening == 0 且 PUR 侧有 opening/audited（含仅 PUR adj）
 *   → rawCos=0，走 OI+PUR_audited+CI（Client 基线 + PUR adj；忽略伴生 COS adj）
 * - COS opening != 0，或仅 COS 侧有 audited（仅 COS adj、PUR 无数）
 *   → 用 cosAuditedRaw（COS opening+COS adj，或已公式化的叶子 Audited）
 */
export function resolveRawCosForPlCotParentAuditedRollup(params: {
    cosOpening: number;
    purOpening: number;
    /** Leaf Audited COS or opening+COS adj（含真实 COS adj） */
    cosAuditedRaw: number;
    /** PUR opening + PUR adj after COT overrides */
    purAudited: number;
}): number {
    const cosOpen = isNonZero(params.cosOpening);
    const purOpen = isNonZero(params.purOpening);
    const purAud = isNonZero(params.purAudited);
    const cosAud = isNonZero(params.cosAuditedRaw);

    // PUR 录入路径（含仅有 PUR adjustment）：禁止伴生 COS adj 短路父汇总
    if (!cosOpen && (purOpen || purAud)) {
        return 0;
    }
    // COS 录入，或仅 COS adjustment（opening 均为 0、PUR 无数）
    if (cosOpen || (cosAud && !purOpen && !purAud)) {
        return Number(params.cosAuditedRaw) || 0;
    }
    return 0;
}

/** Client 与 Audited 两侧 raw COS 均为 0 时不展示 COS 行（公式只参与父汇总） */
export function shouldHidePlCotCosRowWhenRawZero(
    rawCosClient: number,
    rawCosAudited: number,
): boolean {
    return !isNonZero(rawCosClient) && !isNonZero(rawCosAudited);
}

export interface ApplyPlCotCosPurOverridesParams {
    out: Record<number, number>;
    cotInputMode: PlCotInputMode;
    hideCostOfSalesRow: boolean;
    bsLastYearAudited: number;
    bsClientCurrent: number;
    oiId?: number;
    purId?: number;
    ciId?: number;
    cosId?: number;
    /** COS 底稿 opening（不含 adjustment） */
    rawCosOpening: number;
    /** PUR 在 override 前的值（client 或 audited 已含 adjustment） */
    rawPurValue: number;
    purAdjustments?: number;
    ciAdjustments?: number;
    /** COS 节点 adjustment 合计（Audited 列 base + adjustment） */
    cosAdjustments?: number;
}

/**
 * FS 落库会把公式侧清 0：仅一侧 opening 非 0 时，以该侧为录入源（忽略过期的 cotInputMode）。
 */
export function resolvePlCotEffectiveInputMode(args: {
    cotInputMode: PlCotInputMode;
    rawPurValue: number;
    rawCosOpening: number;
}): PlCotInputMode {
    const purNz = isNonZero(args.rawPurValue);
    const cosNz = isNonZero(args.rawCosOpening);
    if (purNz && !cosNz) return 'PUR';
    if (cosNz && !purNz) return 'COS';
    return args.cotInputMode;
}

/**
 * 按 FS 模式 COS/PUR 输入规则写入 OI/PUR/CI/COS 叶子值。
 * @returns 是否在 adjustment 模式展示 COS 行
 */
export function applyPlCotCosPurOverrides(params: ApplyPlCotCosPurOverridesParams): boolean {
    const {
        out,
        cotInputMode,
        hideCostOfSalesRow,
        bsLastYearAudited,
        bsClientCurrent,
        oiId,
        purId,
        ciId,
        cosId,
        rawCosOpening,
        rawPurValue,
        purAdjustments = 0,
        ciAdjustments = 0,
        cosAdjustments = 0,
    } = params;

    const openingInv = oiId != null && Number.isFinite(oiId) ? bsLastYearAudited : 0;
    const closingInv = (ciId != null && Number.isFinite(ciId) ? -bsClientCurrent : 0) + ciAdjustments;
    const cosAudited = rawCosOpening + cosAdjustments;

    if (oiId != null && Number.isFinite(oiId)) out[oiId] = openingInv;
    if (ciId != null && Number.isFinite(ciId)) out[ciId] = closingInv;

    const purValue = rawPurValue + purAdjustments;
    // WHY: FS 保存后公式侧为 0；若仍按偏好 COS 反推会把 PUR Client 冲成 0
    const effectiveMode = resolvePlCotEffectiveInputMode({
        cotInputMode,
        rawPurValue,
        rawCosOpening,
    });

    if (hideCostOfSalesRow) {
        if (effectiveMode === 'COS' && purId != null && Number.isFinite(purId)) {
            out[purId] = cosAudited - openingInv - closingInv + purAdjustments;
        } else if (isNonZero(cosAudited) && !isNonZero(purValue) && purId != null && Number.isFinite(purId)) {
            // 无存货且金额在 COS：按 COS 反推 PUR，避免 PUR 模式把成本丢掉
            if (cosId != null && Number.isFinite(cosId)) out[cosId] = cosAudited;
            out[purId] = cosAudited - openingInv - closingInv + purAdjustments;
        } else if (effectiveMode === 'PUR' && purId != null && Number.isFinite(purId)) {
            out[purId] = purValue;
        }
        return false;
    }

    if (effectiveMode === 'COS') {
        if (cosId != null && Number.isFinite(cosId)) out[cosId] = cosAudited;
        if (purId != null && Number.isFinite(purId)) {
            out[purId] = (cosId != null ? (out[cosId] ?? cosAudited) : cosAudited) - openingInv - closingInv;
        }
        return true;
    }

    if (purId != null && Number.isFinite(purId)) out[purId] = purValue;
    const showCos = shouldRenderComputedCostOfSalesInAdjustment(
        effectiveMode,
        openingInv,
        closingInv,
        purValue,
    );
    if (showCos && cosId != null && Number.isFinite(cosId)) {
        // PUR 为录入源时 COS 一律公式；勿回退 rawCosOpening（FS 清零后为 0）
        out[cosId] = openingInv + purValue + closingInv;
    }
    return showCos;
}

export interface ApplyPlCotPurCosLinkedByCodeOptions {
    /** COS Client/opening（勿传 Audited final；缺省则退回用 byCode COS，易误判伴生 adj） */
    cosOpening?: number;
    /** PUR Client/opening（缺省退回 byCode PUR） */
    purOpening?: number;
}

/**
 * TaxPL / code→amount maps: link PUR↔COS on audited totals already stored by FS code
 * (same rules as applyPlCotCosPurOverrides / morning resolveRawCos Audited rollup).
 * Mutates byCode in place.
 * @returns whether COS row should be shown for PL-COT primary-cost rollup
 */
export function applyPlCotPurCosLinkedAmountsByCode(
    byCode: Record<string, number>,
    hideCostOfSalesRow: boolean,
    openings?: ApplyPlCotPurCosLinkedByCodeOptions,
): boolean {
    if (!byCode) return false;

    const oiAmt = Number(byCode['PL-COT-OI']) || 0;
    const purAmt = Number(byCode['PL-COT-PUR']) || 0;
    const ciAmt = Number(byCode['PL-COT-CI']) || 0;
    const cosAmt = Number(byCode['PL-COT-COS']) || 0;
    // WHY: 录入源必须看 opening；用 Audited final 当 opening 会把伴生 COS+PUR 误判为 COS 模式
    const cosOpening = openings?.cosOpening != null ? Number(openings.cosOpening) || 0 : cosAmt;
    const purOpening = openings?.purOpening != null ? Number(openings.purOpening) || 0 : purAmt;

    const hide = hideCostOfSalesRow || (!isNonZero(oiAmt) && !isNonZero(ciAmt));
    const effectiveMode = resolvePlCotEffectiveInputMode({
        cotInputMode: 'COS',
        rawPurValue: purOpening,
        rawCosOpening: cosOpening,
    });

    if (hide) {
        if (effectiveMode === 'COS') {
            byCode['PL-COT-PUR'] = cosAmt - oiAmt - ciAmt;
        } else if (isNonZero(cosAmt) && !isNonZero(purAmt) && !isNonZero(purOpening)) {
            byCode['PL-COT-COS'] = cosAmt;
            byCode['PL-COT-PUR'] = cosAmt - oiAmt - ciAmt;
        } else {
            // PUR 录入（含 opening=0 但 PUR Audited/adj 非 0）：清伴生 COS，主成本走 OI+PUR+CI
            byCode['PL-COT-PUR'] = purAmt;
            byCode['PL-COT-COS'] = oiAmt + purAmt + ciAmt;
        }
        const purAfter = Number(byCode['PL-COT-PUR']) || 0;
        const cosAfter = Number(byCode['PL-COT-COS']) || 0;
        if (!isNonZero(cosAfter) && isNonZero(purAfter)) {
            byCode['PL-COT-COS'] = purAfter;
        }
        return false;
    }

    if (effectiveMode === 'COS') {
        byCode['PL-COT-COS'] = cosAmt;
        byCode['PL-COT-PUR'] = cosAmt - oiAmt - ciAmt;
        return true;
    }

    byCode['PL-COT-PUR'] = purAmt;
    const showCos = shouldRenderComputedCostOfSalesInAdjustment('PUR', oiAmt, ciAmt, purAmt);
    if (showCos) {
        byCode['PL-COT-COS'] = oiAmt + purAmt + ciAmt;
    } else {
        // WHY: 隐藏公式 COS 行时仍写入 COS 槽，避免伴生 COS adj 当 primaryCost
        byCode['PL-COT-COS'] = oiAmt + purAmt + ciAmt;
    }
    return showCos;
}

/** PL-COT-PUR（仅 ProfitLoss adjustment 模式）：后端 adjustment[] 各项均为 0 时返回 true */
export function isPlCotPurAllAdjustmentsZero(node: any): boolean {
    if (!node || !isCode(node, 'PL-COT-PUR')) return false;
    if (!Array.isArray(node.adjustment) || node.adjustment.length === 0) return true;
    return node.adjustment.every((adj: any) => Math.abs(Number(adj?.adjustment) || 0) < 0.01);
}

/** PL-COT-PUR 节点自身 Audited：openingAmount + Σadjustment */
export function resolvePlCotPurSelfAuditedValue(node: any): number {
    const opening = Number((node as any)?.openingAmount ?? (node as any)?.amount ?? 0) || 0;
    if (!Array.isArray(node?.adjustment)) return opening;
    const adjSum = node.adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
    return opening + adjSum;
}

/** COS/BS 公式 override 后：PUR adjustment 全 0 时回写节点自身 Audited。
 * 若自身 opening 为 0 但 out 已有 COS 反推金额（无存货、GL 只挂 PL-COT-COS），保留反推值。
 */
export function applyPlCotPurSelfAuditedIfAllAdjustmentsZero(
    out: Record<number, number>,
    purNode: any,
): void {
    if (!purNode || !isPlCotPurAllAdjustmentsZero(purNode)) return;
    const purId = Number((purNode as any)?.fsId);
    if (!Number.isFinite(purId)) return;
    const selfAudited = resolvePlCotPurSelfAuditedValue(purNode);
    const derived = Number(out[purId]) || 0;
    // 自身无数、out 已有 COS→PUR 反推：勿冲掉（Chi Ying 等 GL Purchase→PL-COT-COS）
    if (!isNonZero(selfAudited) && isNonZero(derived)) return;
    out[purId] = selfAudited;
}

export interface ResolvePlCotCosAdjustmentSumParams {
    fsTree?: any[] | null;
    cotInputMode: PlCotInputMode;
    showCosRowInAdjustment: boolean;
    /** 指定 refNo；省略则汇总全部 adjustment 条目 */
    refNo?: string | null;
    /** manual 列：排除 isAutoAdj */
    excludeAuto?: boolean;
    isAutoAdjEntry?: (adj: any) => boolean;
    /** Auto 列：仅统计匹配的 auto adjustment */
    autoOnly?: boolean;
    autoAdjType?: string;
    autoRefNo?: string;
}

function sumPlCotComponentAdjustments(
    node: any,
    params: ResolvePlCotCosAdjustmentSumParams,
): number {
    if (!node || !Array.isArray(node.adjustment)) return 0;
    const {
        refNo,
        excludeAuto = false,
        isAutoAdjEntry,
        autoOnly = false,
        autoAdjType,
        autoRefNo,
    } = params;
    return node.adjustment.reduce((sum: number, adj: any) => {
        if (refNo != null && String(adj?.refNo) !== refNo) return sum;
        const isAuto = isAutoAdjEntry?.(adj) ?? false;
        if (excludeAuto && isAuto) return sum;
        if (autoOnly) {
            if (!isAuto) return sum;
            // Prefer autoAdjType so TAX vs TAX_EXCHANGE never cross-match via refNo
            if (autoAdjType != null && String(autoAdjType).trim() !== '') {
                const adjType = String(adj?.autoAdjType || '').trim();
                if (adjType) {
                    if (adjType !== String(autoAdjType).trim()) return sum;
                } else if (String(adj?.refNo) !== String(autoRefNo ?? '')) {
                    return sum;
                }
            } else if (autoRefNo != null && String(adj?.refNo) !== String(autoRefNo)) {
                return sum;
            }
        }
        return sum + (Number(adj?.adjustment) || 0);
    }, 0);
}

/**
 * adjustment 模式 PL-COT-COS 的 Adjust 列：COS = OI + PUR + CI 时按公式汇总子节点 adjustment。
 */
export function resolvePlCotCostOfSalesAdjustmentSum(
    params: ResolvePlCotCosAdjustmentSumParams,
): number {
    const {
        fsTree,
        cotInputMode,
        showCosRowInAdjustment,
    } = params;
    const oiNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
    const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
    const ciNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
    const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);

    const oiAdj = sumPlCotComponentAdjustments(oiNode, params);
    const purAdj = sumPlCotComponentAdjustments(purNode, params);
    const ciAdj = sumPlCotComponentAdjustments(ciNode, params);
    const cosDirect = sumPlCotComponentAdjustments(cosNode, params);

    if (cotInputMode === 'PUR' && showCosRowInAdjustment) {
        return oiAdj + purAdj + ciAdj;
    }

    if (cotInputMode === 'COS') {
        const useFormula = isNonZero(purAdj) || isNonZero(ciAdj) || isNonZero(oiAdj);
        if (useFormula) {
            return oiAdj + purAdj + ciAdj;
        }
        return cosDirect;
    }

    return cosDirect;
}

export interface ShouldUsePlCotFormulaCosAuditedParams {
    cotInputMode: PlCotInputMode;
    showCosRowInAdjustment: boolean;
    hideCostOfSalesRow: boolean;
    purAdjustments?: number;
    ciAdjustments?: number;
    oiAdjustments?: number;
    openingInv?: number;
    bsClientCurrent?: number;
}

/** adjustment 模式 PL-COT-COS Audited 是否按 OI+PUR+CI 公式汇总（与 applyPlCotCosPurOverrides 一致） */
export function shouldUsePlCotFormulaCosAudited(params: ShouldUsePlCotFormulaCosAuditedParams): boolean {
    const {
        cotInputMode,
        showCosRowInAdjustment,
        hideCostOfSalesRow,
        purAdjustments = 0,
        ciAdjustments = 0,
        oiAdjustments = 0,
        openingInv = 0,
        bsClientCurrent = 0,
    } = params;
    if (hideCostOfSalesRow || !showCosRowInAdjustment) return false;
    if (cotInputMode === 'PUR') return true;
    if (cotInputMode === 'COS') {
        return isNonZero(purAdjustments)
            || isNonZero(ciAdjustments)
            || isNonZero(oiAdjustments)
            || isNonZero(openingInv)
            || isNonZero(bsClientCurrent);
    }
    return false;
}

export function sumPlCotComponentAuditedValues(
    leafAudited: Record<number, number>,
    oiId?: number,
    purId?: number,
    ciId?: number,
): number {
    const oi = oiId != null && Number.isFinite(oiId) ? (Number(leafAudited[oiId]) || 0) : 0;
    const pur = purId != null && Number.isFinite(purId) ? (Number(leafAudited[purId]) || 0) : 0;
    const ci = ciId != null && Number.isFinite(ciId) ? (Number(leafAudited[ciId]) || 0) : 0;
    return oi + pur + ci;
}

/** FS 模式录入的 Last Year 优先于 adjustment 树节点上的 lastYearAudited */
export function resolvePlLastYearLeafAmount(
    fsId: number | undefined,
    plLastYearValues: Record<number, string> | null | undefined,
    treeFallback = 0,
): number {
    if (fsId != null && Number.isFinite(fsId) && plLastYearValues) {
        const raw = plLastYearValues[fsId];
        if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
            const parsed = Number(String(raw).replace(/,/g, ''));
            if (Number.isFinite(parsed)) return parsed;
        }
    }
    return Number(treeFallback) || 0;
}

const COT_AMOUNT_EPS = 0.01;

function coercePlCotAmount(map: Record<number, string>, fsId: number | undefined): number {
    if (fsId == null || !Number.isFinite(fsId)) return 0;
    const raw = map[fsId];
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

export interface InferPlCotInputModeParams {
    plTree?: any[] | null;
    /** BS 树：用于找 BS-CA-INV（存货不在 PL 树上） */
    bsTree?: any[] | null;
    plValuesMap: Record<number, string>;
    bsValuesMap?: Record<number, string>;
    bsLastYearMap?: Record<number, string>;
}

/**
 * 从已保存的 PL/BS 金额推断 FS 模式下 COT 用户输入的是 COS 还是 PUR。
 *
 * 规则（后端/落库渲染）：
 * - COS == 0 且 PUR != 0 → PUR 可编
 * - PUR == 0（含双侧皆 0）→ COS 可编（初始化默认）
 * - 双侧皆非 0 → 按公式一致性推断，仍默认可回落到 COS
 */
export function inferPlCotInputMode(params: InferPlCotInputModeParams): PlCotInputMode {
    const {plTree, bsTree, plValuesMap, bsValuesMap = {}, bsLastYearMap = {}} = params;

    const oiNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-OI']);
    const purNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-PUR']);
    const ciNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-CI']);
    const cosNode = findNodeByCodes((plTree || []) as any[], ['PL-COT-COS']);
    // 存货在 BS 树；旧代码在 plTree 上找 BS-CA-INV 永远落空 → OI/CI 当 0
    const invNode = findNodeByCodes((bsTree || []) as any[], ['BS-CA-INV'])
        || findNodeByCodes((plTree || []) as any[], ['BS-CA-INV']);

    const oiId = Number((oiNode as any)?.fsId);
    const purId = Number((purNode as any)?.fsId);
    const ciId = Number((ciNode as any)?.fsId);
    const cosId = Number((cosNode as any)?.fsId);
    const invId = Number((invNode as any)?.fsId);

    const bsInvCurrent = coercePlCotAmount(bsValuesMap, invId);
    const bsInvLastYear = coercePlCotAmount(bsLastYearMap, invId);

    const oi = bsInvLastYear || coercePlCotAmount(plValuesMap, oiId);
    const ci = (bsInvCurrent ? -bsInvCurrent : 0) || coercePlCotAmount(plValuesMap, ciId);
    const cos = coercePlCotAmount(plValuesMap, cosId);
    const pur = coercePlCotAmount(plValuesMap, purId);

    const cosNz = isNonZero(cos);
    const purNz = isNonZero(pur);
    // 一侧为 0：以有数侧为录入源；双侧 0：默认 COS 可编
    if (!cosNz && purNz) return 'PUR';
    if (!purNz) return 'COS';

    // 双侧皆非 0：按 OI/PUR/CI 公式一致性判断录入源
    const impliedPurFromCos = cos - oi - ci;
    const impliedCosFromPur = oi + pur + ci;

    const purMatchesCosDerived = Math.abs(pur - impliedPurFromCos) < COT_AMOUNT_EPS;
    const cosMatchesPurDerived = Math.abs(cos - impliedCosFromPur) < COT_AMOUNT_EPS;

    if (cosMatchesPurDerived && purMatchesCosDerived) return 'COS';
    if (cosMatchesPurDerived && !purMatchesCosDerived) return 'PUR';
    if (!cosMatchesPurDerived && purMatchesCosDerived) return 'COS';
    return 'COS';
}

/**
 * 合并 localStorage 偏好与按金额推断的模式。
 * 一侧为 0 / 双侧为 0 时以金额规则为准，避免过期偏好冲掉后端有数侧。
 */
export function resolvePlCotInputMode(
    stored: PlCotInputMode | null | undefined,
    params: InferPlCotInputModeParams,
): PlCotInputMode {
    const inferred = inferPlCotInputMode(params);

    const purNode = findNodeByCodes((params.plTree || []) as any[], ['PL-COT-PUR']);
    const cosNode = findNodeByCodes((params.plTree || []) as any[], ['PL-COT-COS']);
    const pur = coercePlCotAmount(params.plValuesMap, Number((purNode as any)?.fsId));
    const cos = coercePlCotAmount(params.plValuesMap, Number((cosNode as any)?.fsId));
    const cosNz = isNonZero(cos);
    const purNz = isNonZero(pur);

    // 金额零值规则优先于 localStorage 偏好
    if (!cosNz && purNz) return 'PUR';
    if (!purNz) return 'COS';

    if (!stored) return inferred;
    return stored;
}
